import React from 'react';
import DraggableBox from './DraggableBox';

const priceText = (value) => {
  if (value == null || value === '') return '—';
  return Number(value).toLocaleString('en-GB', { maximumFractionDigits: 4 });
};

const OrderPanel = ({
  symbol,
  market,
  marketPrice,
  orderType,
  setOrderType,
  quantity,
  setQuantity,
  riskPerTrade,
  limitPrice,
  setLimitPrice,
  stopPrice,
  setStopPrice,
  pickMode,
  setPickMode,
  onPlace,
  busy,
  error,
  alreadyOwned,
  tradingMode = 'training',
  onClose,
  ibkrTrading,
  ibkrAccounts = [],
  ibkrAccount,
  setIbkrAccount,
  ibkrOrderType,
  setIbkrOrderType,
  ibkrLimitPrice,
  setIbkrLimitPrice,
  onIbkrOrder,
  ibkrBusy,
  ibkrError,
  ibkrResult,
}) => {
  const ibkrDisabled = !symbol || ibkrBusy || !ibkrAccount || !(Number(quantity) > 0)
    || (ibkrOrderType === 'LMT' && !(Number(ibkrLimitPrice) > 0));
  return (
  <DraggableBox className="order-panel" storageKey="screener2-order-box-pos">
    {/* Train hides ticker/price - they're not tracked there. */}
    {tradingMode === 'live' && (
      <div className="order-heading">
        <span>{symbol ? `${market}:${symbol}` : 'Select a stock'}</span>
        <div className="order-heading-right">
          <span className="paper-badge live-badge">LIVE</span>
          {onClose && <button type="button" className="order-close" onClick={onClose} aria-label="Close order box" title="Hide order box">✕</button>}
        </div>
      </div>
    )}
    {tradingMode !== 'live' && (
      <div className="order-heading">
        <span className="paper-badge">PAPER</span>
        {onClose && <button type="button" className="order-close" onClick={onClose} aria-label="Close order box" title="Hide order box">✕</button>}
      </div>
    )}

    {/* Shown before the order line so on mobile it pushes the order controls
        out of view - no point reaching the Buy form when you already own it. */}
    {alreadyOwned && (
      <div className="order-error already-owned-note">
        Already owned — close the existing order before buying again.
      </div>
    )}

    {tradingMode === 'live' && (
      <div className="order-market-price">
        <strong>{priceText(marketPrice)}</strong>
      </div>
    )}

    {tradingMode !== 'live' && (
      <div className="order-type-toggle">
        <button type="button" className={orderType === 'market' ? 'active' : ''} onClick={() => setOrderType('market')}>Market</button>
        <button type="button" className={orderType === 'limit' ? 'active' : ''} onClick={() => setOrderType('limit')}>Limit</button>
      </div>
    )}

    {orderType === 'limit' && (
      <label className="order-field">
        <span>Limit entry</span>
        <div className="price-picker-field">
          <input type="number" min="0" step="0.0001" value={limitPrice} onChange={(event) => setLimitPrice(event.target.value)} />
        </div>
      </label>
    )}

    <div className="field-row order-entry-row">
      <label className="order-field quantity-field">
        <span>Quantity (${Number(riskPerTrade || 200).toLocaleString('en-US')} risk)</span>
        <input
          type="number"
          min="1"
          step="1"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          onBlur={() => {
            const wholeQuantity = Math.floor(Number(quantity));
            if (wholeQuantity > 0) setQuantity(String(wholeQuantity));
          }}
          title="Suggested from profile risk and stop loss; you can override it"
        />
      </label>
      <label className="order-field stop-field">
        <span>Stop loss (optional)</span>
        <div className="price-picker-field">
          <input type="number" min="0" step="0.0001" value={stopPrice} onChange={(event) => setStopPrice(event.target.value)} />
          <button type="button" className={pickMode === 'stop' ? 'picking' : ''} onClick={() => setPickMode(pickMode === 'stop' ? '' : 'stop')}>
            Pick
          </button>
        </div>
      </label>
      <button
        type="button"
        className="place-order"
        disabled={!symbol || busy || alreadyOwned || !(Number(quantity) > 0)
          || (orderType === 'limit' && !(Number(limitPrice) > 0))}
        onClick={onPlace}
        title={alreadyOwned
          ? 'Already have an open or pending order for this symbol'
          : "Quantity and limit entry (when used) must be positive; stop loss is optional"}
      >
        {busy ? 'Placing…' : 'Buy'}
      </button>
    </div>

    {pickMode && <div className="pick-hint">Click the chart to set the {pickMode === 'entry' ? 'limit entry' : 'stop loss'}.</div>}
    {error && <div className="order-error">{error}</div>}

    {/* Real broker orders. Deliberately separate from Buy above, which only
        writes to this app: nothing here should be reachable by habit. */}
    {tradingMode === 'live' && ibkrTrading && (
      <div className={`ibkr-order-block ${ibkrTrading.target === 'live' ? 'is-live' : 'is-paper'}`}>
        <div className="ibkr-order-heading">
          <span>Send to IBKR</span>
          <span className={`paper-badge ${ibkrTrading.target === 'live' ? 'live-badge' : ''}`}>
            {ibkrTrading.target === 'live' ? 'REAL MONEY' : 'PAPER ACCT'}
          </span>
        </div>
        <div className="field-row">
          <label className="order-field">
            <span>Account</span>
            <select value={ibkrAccount} onChange={(event) => setIbkrAccount(event.target.value)}>
              {ibkrAccounts.length === 0 && <option value="">No account</option>}
              {ibkrAccounts.map((account) => <option key={account} value={account}>{account}</option>)}
            </select>
          </label>
          <label className="order-field">
            <span>Type</span>
            <select value={ibkrOrderType} onChange={(event) => setIbkrOrderType(event.target.value)}>
              <option value="MKT">Market</option>
              <option value="LMT">Limit</option>
            </select>
          </label>
        </div>
        {ibkrOrderType === 'LMT' && (
          <label className="order-field">
            <span>Limit price</span>
            <input type="number" min="0" step="0.0001" value={ibkrLimitPrice}
              onChange={(event) => setIbkrLimitPrice(event.target.value)} />
          </label>
        )}
        <div className="ibkr-order-actions">
          <button type="button" className="ibkr-buy" disabled={ibkrDisabled}
            onClick={() => onIbkrOrder('BUY')}
            title={`Send a real BUY to IBKR account ${ibkrAccount}`}>
            {ibkrBusy ? 'Sending…' : 'Buy @ IBKR'}
          </button>
          <button type="button" className="ibkr-sell" disabled={ibkrDisabled}
            onClick={() => onIbkrOrder('SELL')}
            title={`Send a real SELL to IBKR account ${ibkrAccount}`}>
            {ibkrBusy ? 'Sending…' : 'Sell @ IBKR'}
          </button>
        </div>
        <p className="order-note">
          Single order limit ${Number(ibkrTrading.maxOrderUsd).toLocaleString('en-US')}. A market order is
          valued at the last close plus 5% for that check, so the fill can differ.
        </p>
        {ibkrError && <div className="order-error">{ibkrError}</div>}
        {ibkrResult && <div className="ibkr-order-result">{ibkrResult}</div>}
      </div>
    )}
    <p className="order-note">Market orders use this date’s close. Limit orders and stops use daily candles.</p>
  </DraggableBox>
  );
};

export default OrderPanel;
